import { isAxiosError } from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { useApiRequest } from '@peanuds/composables';
import { generateFingerprint, peanudsApi, withAuthenticatedHeaders } from '@peanuds/shared';
import type {
  AuthenticatedOptions,
  IdempotencySubmission,
  Options,
  UpdateBeneficiaryBankAccountData,
  UpdateBeneficiaryDetailsData,
} from '@peanuds/shared';

import { withStoreIdempotencyHeaders } from '../idempotency-submission';

type UpdateBeneficiaryDetailsOptions = AuthenticatedOptions<UpdateBeneficiaryDetailsData>;
type SafeUpdateBeneficiaryDetailsOptions = Omit<UpdateBeneficiaryDetailsOptions, 'body'> & {
  body: Omit<UpdateBeneficiaryDetailsOptions['body'], 'paymentRail'>;
};

const resolveBackendErrorBody = (error: unknown): unknown =>
  isAxiosError(error) ? error.response?.data : error;

const getBackendError = (error: unknown): { message: string | null; code: string | null } => {
  const body = resolveBackendErrorBody(error);
  if (typeof body !== 'object' || body === null) {
    return { message: null, code: null };
  }

  const backendError = (body as { error?: { message?: unknown; code?: unknown } }).error;
  const message = backendError?.message;
  const code = backendError?.code;

  return {
    message: typeof message === 'string' && message.length > 0 ? message : null,
    code: typeof code === 'string' && code.length > 0 ? code : null,
  };
};

export const useUpdateBeneficiaryStore = defineStore('update-beneficiary', () => {
  const lastDetailsSubmission = ref<IdempotencySubmission | null>(null);
  const lastBankAccountSubmission = ref<IdempotencySubmission | null>(null);

  const {
    execute: executeUpdateBeneficiaryDetails,
    isLoading: isUpdatingDetails,
    error: detailsError,
    reset: resetDetailsState,
  } = useApiRequest({
    request: async (options: Options<UpdateBeneficiaryDetailsData>) =>
      peanudsApi.updateBeneficiaryDetails(await withAuthenticatedHeaders(options)),
    applyAuthHeaders: false,
  });

  const {
    execute: executeUpdateBeneficiaryBankAccount,
    isLoading: isUpdatingBankAccount,
    error: bankAccountError,
    reset: resetBankAccountState,
  } = useApiRequest({
    request: async (options: Options<UpdateBeneficiaryBankAccountData>) =>
      peanudsApi.updateBeneficiaryBankAccount(await withAuthenticatedHeaders(options)),
    applyAuthHeaders: false,
  });

  const isUpdating = computed(() => isUpdatingDetails.value || isUpdatingBankAccount.value);
  const updateError = computed(() => detailsError.value ?? bankAccountError.value);
  const updateErrorMessage = computed(() => getBackendError(updateError.value).message);
  const updateErrorCode = computed(() => getBackendError(updateError.value).code);

  const updateBeneficiaryDetails = async (
    options: SafeUpdateBeneficiaryDetailsOptions,
  ): Promise<boolean> => {
    if ('paymentRail' in options.body) {
      throw new Error('Beneficiary payment rail cannot be updated by the frontend');
    }

    const result = await executeUpdateBeneficiaryDetails(
      withStoreIdempotencyHeaders(
        lastDetailsSubmission,
        options,
        generateFingerprint({ path: options.path, body: options.body }),
      ),
    );

    return result !== undefined;
  };

  const updateBeneficiaryBankAccount = async (
    options: AuthenticatedOptions<UpdateBeneficiaryBankAccountData>,
  ): Promise<boolean> => {
    const result = await executeUpdateBeneficiaryBankAccount(
      withStoreIdempotencyHeaders(
        lastBankAccountSubmission,
        options,
        generateFingerprint({ path: options.path, body: options.body }),
      ),
    );

    return result !== undefined;
  };

  const resetUpdateState = (): void => {
    resetDetailsState();
    resetBankAccountState();
    lastDetailsSubmission.value = null;
    lastBankAccountSubmission.value = null;
  };

  return {
    isUpdating,
    updateError,
    updateErrorMessage,
    updateErrorCode,
    updateBeneficiaryDetails,
    updateBeneficiaryBankAccount,
    resetUpdateState,
  };
});
